import { notify } from '@eleveo/components-react';
import { AxiosError, AxiosInstance } from 'axios';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import { ErrorHandlerFunction, ETLErrorResponse } from './types';
import { useAuth } from '../app/AuthorizationProvider';
import { useDataProvider } from '../app/DataProvider';
import { EndpointsFactory } from '../constants/integrationConstants';
import { ConfigurationType } from '../scene/integrations/types';
import { ETL_PERMISSIONS } from '../utils/keycloakUtils';

export const defaultProps = {
  defaultPage: 1,
  defaultPageSize: 25,
  pageSizes: [10, 25, 50, 100],
};

export const CONFIGURATIONS_ROOT_KEY = 'configurations';
export const CONFIGURATION_ROOT_KEY = 'configuration';
export const DEFAULT_PAGE_SIZE = defaultProps.defaultPageSize;
export const DEFAULT_PAGE_SIZES = defaultProps.pageSizes;
export const INITIAL_PAGE = defaultProps.defaultPage;
export const PAGE_PARAM_KEY = 'page';
export const PAGE_SIZE_PARAM_KEY = 'pageSize';

export const handleConfigurationsErrors: ErrorHandlerFunction = (queryKey, t) => {
  const queryRootKey = Array.from(queryKey)[0];
  const isConfigurationsError = queryRootKey === CONFIGURATIONS_ROOT_KEY || queryRootKey === CONFIGURATION_ROOT_KEY;
  if (isConfigurationsError) {
    notify.error(t('myConfigurations.failedFetch'));
  }

  return isConfigurationsError;
};

export const handleMutationConfigurationsError = (
  error: AxiosError<ETLErrorResponse>,
  t: TFunction,
  configurationName: string
) => {
  const code = error.response?.data.code;
  if (code === 409) {
    notify.error(
      t('myConfigurations.response.errorName', {
        configurationName,
      })
    );
    return;
  }

  notify.error(t('myConfigurations.response.error'));
};

const fetchConfigurations =
  (client: AxiosInstance, pageSize: number, page: number, integrationTypes: readonly string[]) => () => {
    // endpoint pagination starts at page 0
    return EndpointsFactory.getConfigurations(client, pageSize, Math.max(0, page - 1), integrationTypes.join(','));
  };

export function useConfigurationsQuery(pageSize: number, page: number, integrationTypes: readonly string[]) {
  const { client } = useDataProvider();
  const { userHasPermissions } = useAuth();

  return useQuery(
    [CONFIGURATIONS_ROOT_KEY, pageSize, page],
    fetchConfigurations(client, pageSize, page, integrationTypes),
    {
      enabled: userHasPermissions([ETL_PERMISSIONS.getConfiguration]),
    }
  );
}

const fetchConfigurationById = (client: AxiosInstance, id: number) => (): Promise<ConfigurationType> =>
  EndpointsFactory.getConfigurationById(client, id);

export function useConfigurationByIdQuery(
  id?: number,
  options: UseQueryOptions<ConfigurationType, Error, ConfigurationType, [typeof CONFIGURATION_ROOT_KEY, number]> = {}
) {
  const { client } = useDataProvider();
  const { userHasPermissions } = useAuth();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useQuery([CONFIGURATION_ROOT_KEY, id!], fetchConfigurationById(client, id!), {
    enabled: !Number.isNaN(Number(id)) && userHasPermissions([ETL_PERMISSIONS.getConfiguration]),
    ...options,
  });
}

export function useDeleteConfigurationById() {
  const { client } = useDataProvider();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<ConfigurationType, AxiosError<ETLErrorResponse>, ConfigurationType>(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (configuration: ConfigurationType) => EndpointsFactory.deleteConfigurationById(client, configuration.id!),
    {
      /**
       * invalidate fetched configuration so far, will result in fetching current active page
       * and discarding other cached pages, also discard currently opened configuration.
       */
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries([CONFIGURATION_ROOT_KEY, data.id]),
          queryClient.invalidateQueries([CONFIGURATIONS_ROOT_KEY]),
        ]),
      onError: () => {
        notify.error(t('myConfigurations.response.error'));
      },
    }
  );
}

export function useCreateConfigurationQuery() {
  const { client } = useDataProvider();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<ConfigurationType, AxiosError<ETLErrorResponse>, ConfigurationType>(
    /**
     * with multiple configurations, determine correct tasks according to type param `configuration.integration`
     * remove task that doesnt need to be sent, but force user to fill at least one task.
     */
    (configuration: ConfigurationType) => EndpointsFactory.createConfiguration(client, configuration),
    {
      /**
       * invalidate fetched configurations so far, will result in fetching current active page
       * and discarding other cached pages;
       */
      onSuccess: () => queryClient.invalidateQueries([CONFIGURATIONS_ROOT_KEY]),
      onError: (error, { configurationName }) => handleMutationConfigurationsError(error, t, configurationName),
    }
  );
}

export function useUpdateConfigurationQuery() {
  const { client } = useDataProvider();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<ConfigurationType, AxiosError<ETLErrorResponse>, ConfigurationType>(
    (configuration: ConfigurationType) => EndpointsFactory.updateConfiguration(client, configuration),
    {
      /**
       * invalidate fetched configuration so far, will result in fetching current active page and discarding other
       * cached pages and also re-fetch currently edited configuration.
       */
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries([CONFIGURATION_ROOT_KEY, data.id]),
          queryClient.invalidateQueries([CONFIGURATIONS_ROOT_KEY]),
        ]),
      onError: (error, { configurationName }) => handleMutationConfigurationsError(error, t, configurationName),
    }
  );
}
